import axios from "axios";
import { getAuth } from "firebase/auth";
import { ADMIN_PASSWORD_STORAGE_KEY } from "../utils/adminApi";

// Cấu hình base URL cho API
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Tạo axios instance với cấu hình
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 25000, // 25 giây - tối ưu cho Heroku timeout 30s
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Lấy fresh Firebase ID token nếu user đăng nhập bằng Google/Email.
 * Firebase ID Token chỉ sống 1 giờ — hàm này tự động refresh token
 * thông qua Firebase SDK (SDK tự cache và chỉ gọi server khi cần).
 * Trả về null nếu user dùng Facebook login (token dạng khác).
 */
async function getFreshFirebaseToken() {
  const loginMethod = localStorage.getItem("loginMethod");
  // Facebook login sử dụng long-lived token riêng, không phải Firebase ID token
  if (loginMethod === "facebook") {
    return null;
  }

  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (currentUser) {
      // forceRefresh=false: SDK tự quyết định refresh khi token gần hết hạn
      const freshToken = await currentUser.getIdToken(false);
      // Cập nhật localStorage để các component khác cũng dùng token mới
      localStorage.setItem("userAccessToken", freshToken);
      return freshToken;
    }
  } catch (error) {
    console.warn("[apiClient] Không thể refresh Firebase token:", error.message);
  }

  return null;
}

// Request interceptor — auto-refresh Firebase token trước mỗi request
apiClient.interceptors.request.use(
  async (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    config.headers = config.headers || {};
    const storedAdminPassword = localStorage.getItem(ADMIN_PASSWORD_STORAGE_KEY);
    const adminPassword =
      storedAdminPassword ||
      process.env.REACT_APP_ADMIN_PASSWORD ||
      process.env.ADMIN_PASSWORD;

    if (localStorage.getItem("adminAuthenticated") === "true" && adminPassword) {
      config.headers["X-Admin-Password"] = adminPassword;
    }

    if (!config.headers.Authorization) {
      // Ưu tiên lấy fresh token từ Firebase Auth (Google/Email login)
      const freshToken = await getFreshFirebaseToken();
      const token = freshToken || localStorage.getItem("userAccessToken");

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor — retry 1 lần khi nhận 401 (token expired)
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Nếu nhận 401 và chưa retry → thử refresh token rồi retry
    if (
      error.response?.status === 401 &&
      !originalRequest._retried &&
      error.response?.data?.error === "TOKEN_INVALID"
    ) {
      originalRequest._retried = true;

      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (currentUser) {
          // forceRefresh=true: bắt buộc lấy token mới từ Firebase server
          const newToken = await currentUser.getIdToken(true);
          localStorage.setItem("userAccessToken", newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          console.log("[apiClient] Token refreshed, retrying request...");
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error("[apiClient] Token refresh failed:", refreshError.message);
      }
    }

    console.error("API Response Error:", error);
    if (error.response) {
      console.error("Error data:", error.response.data);
      console.error("Error status:", error.response.status);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
export { API_BASE_URL };
